import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from './api';

export interface SyncTceConferenciaPayload {
  municipio: string;
  exercicio: number;
  mesInicio?: number;
  mesFim?: number;
  tipos?: string[];
  anosEmpenhos?: number[];
}

export interface ConferenciaOptions {
  municipios: {
    codigo?: string;
    nome: string;
    integracaoId?: string;
    anos: string[];
  }[];
  tiposProcesso: { id: 'cont' | 'lic'; label: string }[];
  meses: { id: string; label: string }[];
  secretariasPorMunicipio?: Record<string, string[]>;
}

export interface ConferenciaItem {
  _id: string;
  identificador: string;
  tipoProcesso: 'cont' | 'lic';
  tipoRegistro: string;
  data: string;
  credor: string;
  valor: number | string;
  objeto: string;
  secretaria: string;
  empenho?: string;
  anoEmpenho?: string;
  status: 'DIGITALIZADO' | 'PENDENTE';
  gedDocument?: {
    _id: string;
    name: string;
    pages: number;
    createdAt?: string;
  } | null;
  period?: string;
  naoLocalizadoSim?: boolean;
}

export interface ConferenciaReportResponse {
  kpis: {
    totalSim: number;
    totalDigitalizados: number;
    totalPendentes: number;
    totalNaoLocalizadosSim?: number;
    totalTodos?: number;
    taxaDigitalizacao: number;
  };
  secretariasDisponiveis: string[];
  documentTypeGed?: {
    _id: string;
    name: string;
  } | null;
  data: ConferenciaItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  message?: string;
}

export interface ConferenciaFilterParams {
  municipio: string;
  tipoProcesso: 'cont' | 'lic';
  ano: string;
  mes?: string;
  secretaria?: string;
  status?: 'all' | 'digitalizados' | 'pendentes' | 'nao_localizados_sim';
  search?: string;
  page?: number;
  limit?: number;
  refresh?: boolean;
}

export const getConferenciaOptions = async (): Promise<ConferenciaOptions> => {
  const { data } = await api.get('/conferencia/options');
  return data;
};

export const getConferenciaReport = async (
  params: ConferenciaFilterParams
): Promise<ConferenciaReportResponse> => {
  const { data } = await api.get('/conferencia/report', { params });
  return data;
};

export const useConferenciaOptions = () => {
  return useQuery(['conferenciaOptions'], getConferenciaOptions, {
    staleTime: 1000 * 60 * 5, // 5 min
  });
};

export const useConferenciaReport = (params: ConferenciaFilterParams, enabled: boolean = true) => {
  return useQuery(
    ['conferenciaReport', params],
    () => getConferenciaReport(params),
    {
      enabled: enabled && !!params.municipio && !!params.ano,
      keepPreviousData: true,
    }
  );
};

export const syncTceConferencia = async (payload: SyncTceConferenciaPayload) => {
  const { data } = await api.post('/conferencia/sync-tce', payload);
  return data;
};

export const useSyncTceConferencia = () => {
  return useMutation<any, Error, SyncTceConferenciaPayload>(syncTceConferencia);
};
